import { ArrowRight, MessageCircle, ShieldCheck, Factory } from "lucide-react";

export const Hero = () => {
    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative bg-buzas-gray pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden"
        >
            {/* subtle grid backdrop */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-[0.06]"
                style={{
                    backgroundImage:
                        "linear-gradient(#0A2540 1px, transparent 1px), linear-gradient(90deg, #0A2540 1px, transparent 1px)",
                    backgroundSize: "56px 56px",
                    maskImage:
                        "radial-gradient(ellipse at top right, black 40%, transparent 75%)",
                }}
            />

            <div className="container-buzas relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                <div className="max-w-xl">
                    <span
                        data-testid="hero-tag"
                        className="inline-flex items-center gap-2 rounded-sm border border-buzas-navy/15 bg-white px-3 py-1.5 text-[11px] md:text-xs font-bold uppercase tracking-[0.2em] text-buzas-navy"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-buzas-red" />
                        Soluções industriais para o agronegócio
                    </span>

                    <h1
                        data-testid="hero-headline"
                        className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold text-buzas-navy leading-[1.05] tracking-tight"
                    >
                        Peças sob medida para{" "}
                        <span className="text-buzas-red">máquinas agrícolas</span>.
                        <span className="block mt-3 font-bold text-buzas-navy/90 text-2xl sm:text-3xl lg:text-4xl leading-tight">
                            Soluções industriais que movem a indústria de
                            máquinas e equipamentos.
                        </span>
                    </h1>

                    <p
                        data-testid="hero-subheadline"
                        className="mt-6 text-base md:text-lg text-buzas-navy/75 leading-relaxed"
                    >
                        Peças industriais sob medida para o agronegócio, com
                        entrega rápida, resistência estrutural e suporte técnico
                        especializado.
                    </p>

                    <p className="mt-3 text-sm md:text-base text-buzas-gray-500">
                        Mais eficiência no campo começa com peças confiáveis.
                    </p>

                    <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <a
                            href="#contato"
                            data-testid="hero-cta-orcamento"
                            className="group inline-flex items-center justify-center gap-2 rounded-sm bg-buzas-red px-7 py-3.5 text-sm font-bold text-white hover:bg-buzas-red-600 transition-colors"
                        >
                            Solicitar orçamento
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </a>
                        <a
                            href="https://api.whatsapp.com/send?phone=5511940272123&text=Ol%C3%A1%2C%20quero%20falar%20com%20a%20Buzas"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="hero-cta-whatsapp"
                            className="inline-flex items-center justify-center gap-2 rounded-sm border border-buzas-navy/20 bg-white px-7 py-3.5 text-sm font-bold text-buzas-navy hover:bg-buzas-navy hover:text-white transition-colors"
                        >
                            <MessageCircle className="h-4 w-4" />
                            Falar no WhatsApp
                        </a>
                    </div>

                    <div className="mt-10 grid grid-cols-2 gap-6 max-w-md">
                        <div className="flex items-start gap-3">
                            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-buzas-navy text-white">
                                <ShieldCheck className="h-4 w-4" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-buzas-navy">
                                    Alta resistência
                                </p>
                                <p className="text-xs text-buzas-gray-500">
                                    Estruturas testadas
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-buzas-navy text-white">
                                <Factory className="h-4 w-4" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-buzas-navy">
                                    Produção integrada
                                </p>
                                <p className="text-xs text-buzas-gray-500">
                                    Padrão consistente
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative">
                    <div className="relative rounded-sm overflow-hidden border border-buzas-gray-200 shadow-[0_30px_60px_-20px_rgba(10,37,64,0.25)]">
                        <img
                            src="/images/01.png"
                            alt="Estrutura metálica industrial em campo agrícola"
                            className="w-full h-[460px] md:h-[540px] object-cover"
                            loading="eager"
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-buzas-navy/40 via-transparent to-transparent" />
                    </div>

                    {/* floating info card */}
                    <div
                        data-testid="hero-info-card"
                        className="absolute -bottom-6 left-6 right-6 md:left-auto md:right-6 md:w-[320px] bg-white rounded-sm border border-buzas-gray-200 shadow-xl p-5"
                    >
                        <div className="flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-buzas-gray-500">
                            <ShieldCheck className="h-3.5 w-3.5 text-buzas-red" />
                            Qualidade técnica
                        </div>
                        <p className="mt-2 text-base font-bold text-buzas-navy leading-snug">
                            Fabricação sob medida com processos integrados
                        </p>
                    </div>

                    {/* corner badge */}
                    <div className="hidden md:flex absolute -top-4 -left-4 items-center gap-2 bg-buzas-navy text-white px-4 py-2 rounded-sm shadow-lg">
                        <span className="text-2xl font-bold leading-none">+30</span>
                        <span className="text-[11px] font-bold uppercase tracking-[0.18em] leading-tight">
                            anos de<br />indústria
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
};
